/**
 * Shared DICOM preview frame loader.
 *
 * Extracted from useSeriesThumbnails.ts so both the series-sidebar thumbnails
 * and the stone-finding thumbnail render (src/stones/stoneThumbnail.ts) share
 * one wadouri frame-load path (DRY).
 */

import * as cornerstone from "@cornerstonejs/core";
import { DicomInstanceInfo, buildS3ImageIds } from "./dicomMetadataLoader";
import { initializeCornerstone } from "./dicomLoader";

// Type for pixel data from Cornerstone
export type PixelDataArray =
  | Int8Array
  | Int16Array
  | Int32Array
  | Uint8Array
  | Uint8ClampedArray
  | Uint16Array
  | Uint32Array
  | Float32Array
  | Float64Array;

export type PreviewFrameGeometry = Pick<
  DicomInstanceInfo,
  "rows" | "columns" | "image_position_patient" | "image_orientation_patient" | "pixel_spacing"
>;

/** Read the decoded DICOM header, not the eager cache which can contain synthesized IPP. */
export function readPreviewFrameGeometry(image: any): PreviewFrameGeometry | undefined {
  const vector = (tag: string, length: number) => {
    const raw = image.data?.string?.(tag);
    const values = typeof raw === "string" ? raw.split("\\").map(Number) : [];
    return values.length === length && values.every(Number.isFinite) ? values : undefined;
  };
  const origin = vector("x00200032", 3);
  const orientation = vector("x00200037", 6);
  const spacing = vector("x00280030", 2);
  if (!origin || !orientation || !spacing || spacing.some((value) => value <= 0)) return undefined;
  return {
    rows: image.rows,
    columns: image.columns,
    image_position_patient: origin,
    image_orientation_patient: orientation,
    pixel_spacing: spacing.join("\\"),
  };
}

/**
 * Load a DICOM frame from CloudFront → a2z-canonical via Cornerstone's wadouri loader.
 */
export async function loadS3PreviewFrame(
  cdnBaseUrl: string,
  instance: DicomInstanceInfo,
): Promise<{
  pixelData: PixelDataArray;
  rows: number;
  columns: number;
  rescale: { slope: number; intercept: number };
  geometry?: PreviewFrameGeometry;
}> {
  // Ensure Cornerstone is initialized (registers the wadouri loader)
  await initializeCornerstone();

  // Reuse buildS3ImageIds for consistent URL building (DRY)
  const [imageId] = buildS3ImageIds(cdnBaseUrl, [instance]);

  // Add timeout to prevent infinite loading
  // IMPORTANT: Attach catch handler BEFORE Promise.race to prevent "Uncaught (in promise)" errors
  // when the losing promise later rejects (e.g., timeout wins but image later fails)
  // Cold-start S3 metadata fetches can run 3-7s on a fresh backend (see
  // [PERF] logs in s3_dicom_services), so 8s left several thumbnails timing
  // out the first time a case is opened against a cold _metadata_cache. 20s
  // covers the cold path; warm path is still <500ms.
  const timeoutMs = 20000;
  let timeoutId: ReturnType<typeof setTimeout>;

  const imagePromise = cornerstone.imageLoader.loadAndCacheImage(imageId);

  // Attach catch handler immediately to prevent unhandled rejection if timeout wins
  imagePromise.catch(() => {
    /* swallow late rejections */
  });

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error(`Thumbnail load timeout after ${timeoutMs}ms`)),
      timeoutMs,
    );
  });

  const image = await Promise.race([imagePromise, timeoutPromise]);
  clearTimeout(timeoutId!); // Non-null assertion: Promise executor runs synchronously

  return {
    pixelData: image.getPixelData(),
    rows: image.rows,
    columns: image.columns,
    geometry: readPreviewFrameGeometry(image),
    rescale:
      image.preScale?.scaled === true
        ? { slope: 1, intercept: 0 }
        : {
            slope: image.slope ?? instance.rescale_slope ?? 1,
            intercept: image.intercept ?? instance.rescale_intercept ?? 0,
          },
  };
}
